// The Invoice Queue: everything not yet approved, oldest first. Two cards, Needs you (stopped on a
// check, one lead action per row) and Ready to approve (matched, waiting for the batch, with the batch actions);
// what an upload's invoices are doing shows on the upload's own card. A row being worked on keeps its place and
// shows the step in its last column; when it finishes it shows the result, holds, then slides up (app.css .avp-leave).
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import type { Batch, Invoice } from "../api/types";
import { useApiMutation, useBatches, useLive, useLiveInvoices, useNeedsYou, usePrefetchInvoice, useSyncore } from "../api/hooks";
import { useSession } from "../auth/SessionProvider";
import { failedCheckOf, failedWording, plainSummary } from "../shared/checks";
import { CardHead, CheckIcon, ErrorCard, LoadingRows, PageHeader, Spinner, mono, muted, table, td, th } from "../shared/components";
import { ago, count, fmtDateTime, fmtTime, money, outcomeOf, poRef, signed, stepWords, type Outcome } from "../shared/format";
import { ChecksPassed } from "../shared/invoiceParts";
import { LadderButton, LadderMenu, Working, ladderFor } from "../shared/ladder";
import { UploadPanel, useRecentUploads, useUploader } from "../shared/uploads";
import { Button, Card, Icon, Select, Tabs, useToast } from "../ui";
import { Page } from "../shell/Shell";
import { approveLine, useNow } from "./approval";
import { ApproveDialog, PauseDialog } from "./Dialogs";
import { ProblemDetail, ProblemDrawer, useInvoiceActions } from "./ProblemDetail";

const SIZES = [5, 10, 25, 50];
const LEAVE_MS = 2800; // app.css .avp-leave runs 2.6 s (about two seconds held, then a quick fade and collapse); the row is dropped only after that
const MAX_LEAVE_MS = 120_000; // a row whose work never finishes is dropped eventually
const RECEIPT_FOR_MS = 10 * 60_000; // a release that finished this recently still shows its receipt to a page arriving late
const RECEIPT_DISMISSED = "avp-receipt-dismissed"; // the batch whose receipt was dismissed, per tab
const SETTLE_GRACE_MS = 10_000; // once the batch has settled, a row that still has not finished is let go after this long

/**
 * A release as the page owns it from the moment Approve now is confirmed: the rows it had then, oldest first, kept
 * until every one has shown its result and slid up. Server state only refreshes each row's outcome; the batch's own
 * states are not relied on, because a small batch passes through Releasing faster than the page can see it.
 */
interface Release {
  batchId: string;
  startedAt: number;
  total: number;
  /** The rows still in the table, oldest first; a row leaves once its slide has ended. */
  ids: string[];
  /** Each row as it was at confirm, shown until the batch query has something fresher. */
  snap: Record<string, Invoice>;
  /** Each row's final outcome the moment it was final, for the receipt. */
  finished: Record<string, { o: Outcome; total: number | null; at: number }>;
  /** When the batch was seen settled (Released); rows still unfinished after a grace period are let go. */
  settledAt: number | null;
  /** Picked up from a batch already releasing when the page opened: no "time taken", no "picked up after". */
  resumed: boolean;
}
const oldest = (a: Invoice, b: Invoice) => a.receivedAt.localeCompare(b.receivedAt);
const isDone = (o: Outcome) => o !== "wait" && o !== "prog";
// A released row is finished only once it is approved or needs a person: "ready" (matched its PO, parked) is where
// it starts, and the batch's rows still read that way until the API has taken the release.
const releaseFinished = (i: Invoice) => { const o = outcomeOf(i); return isDone(o) && o !== "ready"; };

type Tab = "all" | "needs" | "ready";
type Home = "needs" | "ready";
interface Leaving { snapshot: Invoice; since: number; doneAt: number | null }

/** Received as "4 min ago" or the exact time; the choice is remembered. */
function useTimeMode(): [boolean, () => void] {
  const [rel, setRel] = useState(() => { try { return localStorage.getItem("avp-time") !== "date"; } catch { return true; } });
  const toggle = () => setRel((r) => { try { localStorage.setItem("avp-time", r ? "date" : "ago"); } catch { /* fine */ } return !r; });
  return [rel, toggle];
}

/**
 * Rows that just left a list stay in its table as "leaving" rows: they show the step while their work runs, then the
 * result; `done` is called by the row once its outcome is final, and the row is dropped after the slide-out.
 */
function useLeaving(list: Invoice[], ready: boolean) {
  const seen = useRef<Map<string, Invoice>>(new Map());
  const [leaving, setLeaving] = useState<Record<string, Leaving>>({});
  useEffect(() => {
    const now = new Map(list.map((i) => [i.id, i] as const));
    // While gated off (a release is showing the rows its own way), keep up with the list without animating anything,
    // so rows that left meanwhile are not animated out a second time when the gate reopens.
    if (!ready) { seen.current = now; return; }
    const gone = [...seen.current.entries()].filter(([id]) => !now.has(id));
    seen.current = now;
    setLeaving((l) => {
      let changed = false;
      const n = { ...l };
      const t = Date.now();
      for (const [id, snapshot] of gone) if (!n[id]) { n[id] = { snapshot, since: t, doneAt: null }; changed = true; }
      // A row that came back (checked again and stopped again) is a row again, not a leaving one.
      for (const id of Object.keys(n)) if (now.has(id)) { delete n[id]; changed = true; }
      return changed ? n : l;
    });
  }, [list, ready]);
  const done = useCallback((id: string) => setLeaving((l) => (l[id] && !l[id].doneAt ? { ...l, [id]: { ...l[id], doneAt: Date.now() } } : l)), []);
  const any = Object.keys(leaving).length > 0;
  useEffect(() => {
    if (!any) return;
    const iv = window.setInterval(() => setLeaving((l) => {
      let changed = false;
      const n = { ...l };
      const t = Date.now();
      for (const [id, e] of Object.entries(n)) if ((e.doneAt && t - e.doneAt >= LEAVE_MS) || t - e.since >= MAX_LEAVE_MS) { delete n[id]; changed = true; }
      return changed ? n : l;
    }), 250);
    return () => window.clearInterval(iv);
  }, [any]);
  return { leaving, done };
}

function Pager({ total, page, per, onPage, onPer }: { total: number; page: number; per: number; onPage: (p: number) => void; onPer: (n: number) => void }) {
  if (total <= SIZES[0]) return null;
  const pages = Math.max(1, Math.ceil(total / per));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", ...muted }}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>{(page - 1) * per + 1}–{Math.min(total, page * per)} of {total}</span><span style={{ flex: 1 }} />
      <span>Per page</span>
      <Select size="sm" value={String(per)} onChange={(v) => onPer(Number(v))} options={SIZES.map((s) => ({ value: String(s), label: String(s) }))} style={{ width: 84 }} />
      <Button size="sm" variant="ghost" disabled={page <= 1} leadingIcon={<Icon name="chevron-left" size={14} />} onClick={() => onPage(page - 1)}>Previous</Button>
      <span style={{ fontWeight: 700, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>{page} / {pages}</span>
      <Button size="sm" variant="ghost" disabled={page >= pages} trailingIcon={<Icon name="chevron-right" size={14} />} onClick={() => onPage(page + 1)}>Next</Button>
    </div>
  );
}

function usePaging(total: number) {
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(10);
  const pages = Math.max(1, Math.ceil(total / per));
  const p = Math.min(Math.max(1, page), pages);
  return { page: p, per, setPage, setPer: (n: number) => { setPer(n); setPage(1); }, slice: <T,>(rows: T[]) => rows.slice((p - 1) * per, p * per) };
}

const cw: React.CSSProperties = { display: "block" };

function ReceivedTh({ rel, toggle }: { rel: boolean; toggle: () => void }) {
  return <th style={{ ...th, whiteSpace: "nowrap" }}>Received<button type="button" className="avp-thbtn" onClick={toggle} title={rel ? "Show date and time" : "Show time ago"}>{rel ? "ago" : "date"}</button></th>;
}

function ReceivedTd({ i, rel }: { i: Invoice; rel: boolean }) {
  return <td style={{ ...td, whiteSpace: "nowrap" }}><div className="avp-cw" style={{ ...cw, fontWeight: 600, color: "var(--text-strong)" }} title={fmtDateTime(i.receivedAt)}>{rel ? ago(i.receivedAt) + " ago" : fmtDateTime(i.receivedAt)}</div></td>;
}

/** Supplier, invoice number and PO, stacked; the name is a real button so the row opens from the keyboard. */
function WhoTd({ i, onOpen }: { i: Invoice; onOpen: () => void }) {
  const po = poRef(i);
  return (
    <td style={td}><div className="avp-cw" style={cw}>
      <button type="button" className="avp-rowlink" onClick={(e) => { e.stopPropagation(); onOpen(); }}>{i.supplier ?? i.fileName}</button>
      {i.supplier && <span className="avp-clip" style={{ ...mono, fontWeight: 500, fontSize: 12, color: "var(--text-muted)" }}>{i.invoiceNumber ?? ""}</span>}
      {po && <span className="avp-clip" style={{ ...mono, fontWeight: 500, fontSize: 12, color: "var(--text-muted)" }}>PO {po}</span>}
    </div></td>
  );
}

function TotalTd({ i, currency }: { i: Invoice; currency: string }) {
  return <td className="avp-sm" style={{ ...td, ...mono, textAlign: "right", color: "var(--text-strong)" }}><div className="avp-cw" style={cw}>{money(i.total, currency)}</div></td>;
}

/** The result in the last column once work has finished: a large check and a few words, the rest of the row untouched. */
function Result({ o, home, note, code }: { o: Outcome; home: Home; note?: string | null; code?: string | null }) {
  const good = o === "ok" || o === "ready";
  const text = o === "ok" ? (home === "ready" || code === "VOUCHED_IN_SYNCORE" ? "Approved in Syncore" : "Matched and approved") : o === "ready" ? "Matched its PO" : o === "res" ? (/^Replaced by /i.test(note ?? "") ? "Replaced" : "Rejected") : "Still needs you";
  const color = good ? "var(--status-matched-fg)" : o === "res" ? "var(--text-muted)" : "var(--status-review-fg)";
  return (
    <span className="avp-cw" style={{ display: "inline-flex", alignItems: "center", gap: 10, font: "700 14px var(--font-body)", color, whiteSpace: "nowrap", animation: "av-rise var(--dur-slow) var(--ease-out)" }}>
      {good ? <CheckIcon r="ok" size={24} /> : <span style={{ width: 24, height: 24, borderRadius: "50%", background: o === "res" ? "var(--av-gray-400)" : "var(--av-sun-400)", color: o === "res" ? "#fff" : "var(--av-navy-900)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon name={o === "res" ? "archive" : "circle-alert"} size={14} stroke={3} /></span>}
      {text}
    </span>
  );
}

/** A row that left its list: the fresh invoice says whether its work is still running or how it ended. */
function LeavingRow({ entry, home, rel, currency, onDone, onOpen, fresh: given, note, enter }: { entry: Leaving; home: Home; rel: boolean; currency: string; onDone: (id: string) => void; onOpen: () => void; fresh?: Invoice; note?: string; enter?: boolean }) {
  const snap = entry.snapshot;
  // A released row is fed from the batch's rows; a row working on its own reads itself from the live state.
  const own = useLive((d) => d.invoices.find((i) => i.id === snap.id) ?? null, !given);
  const fresh = given ?? own.data ?? undefined;
  const o = outcomeOf(fresh ?? { ...snap, status: "Processing" });
  // A row that left Ready has not finished while it still reads "ready": that is a parked row the release has not taken yet.
  // A row that left on an action ("Rejecting…", "Replacing…") has not finished until the invoice has changed: its first
  // fetch can land before the API has acted, when it still reads as needing a person.
  const changed = !!fresh && fresh.updatedAt !== snap.updatedAt;
  const done = !!fresh && isDone(o) && !(home === "ready" && o === "ready") && (!note || changed);
  useEffect(() => { if (done) onDone(snap.id); }, [done, onDone, snap.id]);
  const good = o === "ok" || o === "ready";
  const last = done ? <Result o={o} home={home} note={fresh?.resolved?.comment} code={fresh?.code} /> : <Working text={note ? note + "…" : fresh?.step ? stepWords(fresh.step) + "…" : home === "ready" ? "Queued for approval" : "Queued for a check"} />;
  return (
    <tr className={"avp-row" + (enter ? " avp-enter" : "") + (entry.doneAt ? " avp-leave " + (good ? "avp-leave-good" : "avp-leave-plain") : "")}>
      <ReceivedTd i={snap} rel={rel} />
      <WhoTd i={snap} onOpen={onOpen} />
      <TotalTd i={snap} currency={currency} />
      {home === "needs" ? <CheckFailedTd i={snap} /> : <td style={td}><div className="avp-cw" style={cw}><ChecksOk i={snap} /></div></td>}
      <td style={{ ...td, textAlign: home === "needs" ? "right" : "left", whiteSpace: "nowrap" }}><div className="avp-cw" style={cw}>{last}</div></td>
    </tr>
  );
}

function CheckFailedTd({ i }: { i: Invoice }) {
  // A lines difference leads with how far the lines are out; the per-line detail follows.
  const summary = plainSummary(i);
  const why = i.linesDifference != null && failedCheckOf(i) === "Line items" ? `Lines ${signed(i.linesDifference)}${summary ? " – " + summary : ""}` : summary;
  return (
    <td style={td}><div className="avp-cw" style={cw}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, font: "600 13px var(--font-body)", color: "var(--status-mismatch-fg)" }}><CheckIcon r="no" size={16} />{failedWording(failedCheckOf(i))}</span>
      {why && <div className="avp-sub" title={why}>{why}</div>}
    </div></td>
  );
}

/** A parked invoice passed every check (only clean matches wait for the batch); a freight adjustment is a pass by rule. */
function ChecksOk({ i }: { i: Invoice }) {
  // "Would approve after updating freight…", "…filling in freight…", "…lowering freight…", "…adding an Invoice adjustment line…"
  const freight = /(updating|filling in|lowering) freight|leaving the freight/i.test(i.summary ?? "");
  const lines = /adjustment line|leaving the lines/i.test(i.summary ?? "");
  return <ChecksPassed freight={freight} lines={lines} />;
}

function CardEmpty({ title, line, extra }: { title: string; line: string; extra?: ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", padding: "8px 24px 28px" }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ font: "800 20px var(--font-display)", color: "var(--text-strong)" }}>{title}</div>
        <div style={{ font: "var(--type-body)", color: "var(--text-muted)", marginTop: 4 }}>{line}</div>
      </div>
      {extra}
    </div>
  );
}

function todayStart(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** Ready to approve and set aside for a retry: what /invoices calls Held. */
const isHeld = (i: Invoice) => i.status === "Held";

export function InvoiceQueue() {
  const session = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const tab = ((params.get("tab") as Tab) || "all") as Tab;
  const setTab = (t: Tab) => { const next = new URLSearchParams(params); if (t === "all") next.delete("tab"); else next.set("tab", t); next.delete("check"); setParams(next, { replace: true }); };

  // Everything this page shows comes from the live state, which the hub patches as it changes: a release started by
  // anyone, on any API instance, arrives here the moment its API instance hears of it (scale-fix.md).
  const needs = useNeedsYou();
  const held = useLiveInvoices(isHeld);
  const batches = useBatches();
  const since = Date.parse(todayStart());
  const inFlight = useLive((d) => d.invoices.filter((i) => i.status === "Queued" || i.status === "Processing").length).data ?? 0;
  const approvedCount = useLive((d) => d.invoices.filter((i) => i.status === "Vouched" && Date.parse(i.receivedAt) >= since).length).data ?? 0;
  const viewApproved = () => navigate("/reports/invoices?outcome=ok&when=today");
  const approvedLink = approvedCount > 0 ? <a onClick={viewApproved} style={{ cursor: "pointer", font: "700 14px var(--font-body)", whiteSpace: "nowrap" }}>Approved today: {approvedCount}</a> : null;
  const syncore = useSyncore();
  const [drawer, setDrawer] = useState<{ id: string; ids: string[] } | null>(null);
  // Once the invoice in the slide-out is rejected or replaced, the next one in the list opens; after the last, it closes.
  const actions = useInvoiceActions({ onActed: (ids) => setDrawer((dr) => {
    if (!dr || !ids.includes(dr.id)) return dr;
    const i = dr.ids.indexOf(dr.id);
    const id = dr.ids[i + 1] ?? dr.ids[i - 1];
    return id ? { id, ids: dr.ids.filter((x) => x !== dr.id) } : null;
  }) });
  // The invoices either side of the open one are loaded ahead, so moving on doesn't flash a loading state.
  const prefetchInvoice = usePrefetchInvoice();
  useEffect(() => {
    if (!drawer) return;
    const i = drawer.ids.indexOf(drawer.id);
    for (const n of [drawer.ids[i + 1], drawer.ids[i - 1]]) if (n) prefetchInvoice(n);
  }, [drawer, prefetchInvoice]);
  const currency = actions.currency;
  const [rel, toggleRel] = useTimeMode();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"approve" | "pause" | null>(null);
  // One clock for the banner countdown and the "ago" times; it stops while the tab is hidden.
  const tick = useNow(1000);
  useEffect(() => {
    if (!menuFor) return;
    // A press inside the open menu must not close it before the click lands on the item.
    const h = (e: MouseEvent) => { if ((e.target as Element | null)?.closest?.('[role="menu"]')) return; setMenuFor(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuFor]);

  const release = useApiMutation((batch: Batch) => api(`/batches/${batch.id}/release`, { method: "POST", body: {} }));
  const hold = useApiMutation(({ batch, reason }: { batch: Batch; reason: string }) => api(`/batches/${batch.id}/hold`, { method: "POST", body: { reason } }));
  // No toast for an upload: its card on this page says what came of it and what its invoices are doing.
  const uploader = useUploader();
  const recentUploads = useRecentUploads(uploader.items);
  const fileRef = useRef<HTMLInputElement>(null);

  const needList = useMemo(() => (needs.data?.items ?? []).filter((i) => !i.resolved).sort(oldest), [needs.data]);
  const readyList = useMemo(() => (held.data?.items ?? []).filter((i) => i.awaitingRelease).sort(oldest), [held.data]);
  const needLeaving = useLeaving(needList, !!needs.data);
  // The release the page owns, if one is running (see Release above).
  const [rls, setRls] = useState<Release | null>(null);
  // Gated off while a release owns the rows: the release table shows them its own way.
  const readyLeaving = useLeaving(readyList, !!held.data && !rls && !(batches.data?.items ?? []).some((b) => b.state === "Releasing"));
  const needRows = useMemo(() => [...needList, ...Object.values(needLeaving.leaving).map((e) => e.snapshot)].sort(oldest), [needList, needLeaving.leaving]);
  const readyRows = useMemo(() => [...readyList, ...Object.values(readyLeaving.leaving).map((e) => e.snapshot)].sort(oldest), [readyList, readyLeaving.leaving]);
  const needPaging = usePaging(needRows.length);
  const readyPaging = usePaging(readyRows.length);

  // Two lookups: the batch Approve now and Pause act on, and a batch the server says is releasing (for a page that
  // opens mid-release). A new Open batch is opened ahead of a releasing one the moment the release starts, so the
  // release must never be found through the open batch.
  const batchList = useMemo(() => batches.data?.items ?? [], [batches.data]);
  const openBatch = batchList.find((b) => b.state === "Open" || b.state === "Pending" || b.state === "Held") ?? null;
  const releasing = batchList.find((b) => b.state === "Releasing") ?? null;
  const releasingNow = !!rls;
  const releaseMode = !!rls;
  const [faded, setFaded] = useState<Record<string, number>>({});
  const [fadeTick, setFadeTick] = useState(() => Date.now());
  // The rows' live states: the batch's invoices from the live state.
  const batchId = rls?.batchId ?? releasing?.id ?? null;
  const shownBatch = batchId ?? openBatch?.id ?? null;
  const batchInvoices = useLiveInvoices((i) => !!shownBatch && i.batchId === shownBatch, !!shownBatch);
  const batchFresh = useMemo(() => Object.fromEntries((batchInvoices.data?.items ?? []).map((i) => [i.id, i])) as Record<string, Invoice>, [batchInvoices.data]);
  // Opened mid-release with nothing in hand: the releasing batch's rows become the release, as the queue stands now.
  // Rows that finished while the page was away are counted (the progress, the receipt) but never shown: the user
  // sees the current state, not a replay of what they missed.
  useEffect(() => {
    if (rls || !releasing || !batchInvoices.data || batchInvoices.data.items.some((i) => i.batchId !== releasing.id)) return;
    const rows = [...batchInvoices.data.items].sort(oldest);
    if (!rows.length) return;
    const now = Date.now();
    const done = rows.filter(releaseFinished);
    const pending = rows.filter((i) => !releaseFinished(i));
    setRls({
      batchId: releasing.id, startedAt: releasing.releasedAt ? new Date(releasing.releasedAt).getTime() : now, total: rows.length,
      ids: pending.map((i) => i.id), snap: Object.fromEntries(pending.map((i) => [i.id, i])),
      finished: Object.fromEntries(done.map((i) => [i.id, { o: outcomeOf(i), total: i.total ?? null, at: now }])),
      settledAt: null, resumed: true,
    });
    setFaded({});
  }, [rls, releasing, batchInvoices.data, setRls]);
  const releaseRows = useMemo(() => (rls ? rls.ids.map((id) => batchFresh[id] ?? rls.snap[id]).filter((i): i is Invoice => !!i) : []), [rls, batchFresh]);
  const stillFading = !!rls && rls.ids.some((id) => !!faded[id]);
  useEffect(() => {
    if (!rls || !(stillFading || releaseRows.some(releaseFinished))) return;
    const iv = window.setInterval(() => setFadeTick(Date.now()), 250);
    return () => window.clearInterval(iv);
  }, [rls, stillFading, releaseRows]);
  // A row leaves the release once its slide has ended.
  useEffect(() => {
    if (!rls) return;
    const gone = rls.ids.filter((id) => faded[id] && fadeTick - faded[id] >= LEAVE_MS);
    if (gone.length) setRls((r) => (r ? { ...r, ids: r.ids.filter((id) => !gone.includes(id)) } : r));
  }, [rls, faded, fadeTick, setRls]);
  // A row's result is recorded the moment it is final, so the receipt comes from the rows themselves.
  const markFaded = useCallback((id: string) => {
    const now = Date.now();
    setFaded((f) => (f[id] ? f : { ...f, [id]: now }));
    setRls((r) => {
      if (!r || r.finished[id]) return r;
      const i = batchFresh[id] ?? r.snap[id];
      return { ...r, finished: { ...r.finished, [id]: { o: i ? outcomeOf(i) : "wait", total: i?.total ?? null, at: now } } };
    });
  }, [batchFresh, setRls]);
  const releaseEntry = (i: Invoice): Leaving | undefined => (faded[i.id] || releaseFinished(i) ? { snapshot: i, since: faded[i.id] ?? fadeTick, doneAt: faded[i.id] ?? null } : undefined);
  // The batch has settled but a row still has not finished (a stale step, a worker that gave up): a short grace, then it goes.
  useEffect(() => {
    if (!rls || rls.settledAt) return;
    if (batchList.find((b) => b.id === rls.batchId)?.state === "Released") setRls((r) => (r && !r.settledAt ? { ...r, settledAt: Date.now() } : r));
  }, [rls, batchList, setRls]);
  useEffect(() => {
    if (!rls?.settledAt) return;
    const stuck = rls.ids.filter((id) => !faded[id] && !rls.finished[id]);
    if (!stuck.length) return;
    const t = window.setTimeout(() => stuck.forEach((id) => markFaded(id)), Math.max(0, rls.settledAt + SETTLE_GRACE_MS - Date.now()));
    return () => window.clearTimeout(t);
  }, [rls, faded, markFaded]);
  const readyCount = readyList.length;
  const readyValue = readyList.reduce((s, i) => s + (i.total ?? 0), 0);
  // The receipt the banner becomes when a release finishes; it stays until dismissed or the next release.
  const [receipt, setReceipt] = useState<{ batchId: string; approved: number; value: number | null; seconds: number; needs: number } | null>(null);
  const dismissReceipt = () => { if (receipt) { try { sessionStorage.setItem(RECEIPT_DISMISSED, receipt.batchId); } catch { /* per-tab convenience only */ } } setReceipt(null); };
  // Approve now confirmed: the rows on screen become the release at once. If the request fails they are given back.
  const approveNow = () => {
    if (!openBatch || !readyList.length) return;
    const rows = readyList;
    setReceipt(null); setFaded({});
    setRls({ batchId: openBatch.id, startedAt: Date.now(), total: rows.length, ids: rows.map((i) => i.id), snap: Object.fromEntries(rows.map((i) => [i.id, i])), finished: {}, settledAt: null, resumed: false });
    release.mutate(openBatch, { onError: (e) => { setRls(null); toast({ tone: "error", title: "Couldn’t approve", message: e.message }); } });
  };
  // The last row has gone: the receipt, from what the rows themselves reported.
  useEffect(() => {
    if (!rls || rls.ids.length) return;
    const fin = Object.values(rls.finished);
    const approved = fin.filter((f) => f.o === "ok");
    const needs = fin.filter((f) => f.o === "rev" || f.o === "stop" || f.o === "set").length;
    const endAt = fin.reduce((m, f) => Math.max(m, f.at), rls.startedAt);
    if (approved.length || needs) setReceipt({ batchId: rls.batchId, approved: approved.length, value: approved.reduce((s, f) => s + (f.total ?? 0), 0), seconds: rls.resumed ? 0 : Math.max(1, Math.round((endAt - rls.startedAt) / 1000)), needs });
    setRls(null); setFaded({});
  }, [rls, setRls]);
  // Coming back after the batch finished: a release that ended in the last ten minutes still gets its receipt, from
  // the batch's own counts, until it is dismissed. The time taken is unknown to a page that was not watching.
  useEffect(() => {
    if (receipt || releasingNow || releasing) return;
    const last = batchList.find((b) => b.state === "Released" && b.releasedAt && Date.now() - new Date(b.releasedAt).getTime() < RECEIPT_FOR_MS);
    if (!last) return;
    let dismissed: string | null = null;
    try { dismissed = sessionStorage.getItem(RECEIPT_DISMISSED); } catch { /* fine */ }
    if (dismissed === last.id) return;
    const needs = last.counts.needsReview + last.counts.failed;
    if (last.counts.vouched > 0 || needs > 0) setReceipt({ batchId: last.id, approved: last.counts.vouched, value: needs === 0 ? last.value : null, seconds: 0, needs });
  }, [receipt, releasingNow, batchList]);
  // The stages, from the rows: picked up once any row shows a step or has finished; progress is rows finished.
  const releaseTotal = rls?.total ?? 0;
  const releaseDone = rls ? Object.keys(rls.finished).length : 0;
  const pickedUp = !!rls && (releaseDone > 0 || releaseRows.some((i) => !!i.step || i.status === "Processing" || releaseFinished(i)));
  const waitedS = rls ? Math.max(0, Math.round((tick.getTime() - rls.startedAt) / 1000)) : 0;
  // "Picked up after 6 s" only when this page watched the wait; a page opened mid-release just says "Picked up".
  const [pickedAfter, setPickedAfter] = useState<number | null>(null);
  const sawWaiting = useRef(false);
  useEffect(() => {
    if (!releasingNow) { setPickedAfter(null); sawWaiting.current = false; return; }
    if (!pickedUp) { sawWaiting.current = !rls?.resumed; return; }
    if (pickedAfter === null) setPickedAfter(sawWaiting.current ? waitedS : -1);
  }, [releasingNow, pickedUp, waitedS, pickedAfter, rls?.resumed]);
  const paused = openBatch?.state === "Held" ? openBatch : null;
  const policy = actions.policy;
  const readyLine = approveLine(policy, actions.nextRun, tick);
  const nextLine = `${inFlight ? "Processing & comparing " + count(inFlight, "invoice") : "Nothing is being processed right now"}${actions.nextRun ? "; the next batch is approved at " + fmtTime(actions.nextRun) : ""}.`;
  const webBase = syncore.data?.webBaseUrl ?? null;
  const syncoreUrl = useCallback((i: Invoice) => (webBase && i.jobNumber ? webBase.replace(/\/?$/, "/") + "Job/Details/" + i.jobNumber : null), [webBase]);

  // Arriving from the Overview with a failed check chosen: open its oldest invoice.
  const wanted = params.get("check");
  useEffect(() => {
    if (!wanted || !needList.length) return;
    const first = needList.find((i) => failedCheckOf(i) === wanted);
    if (first) setDrawer({ id: first.id, ids: needList.map((i) => i.id) });
    const next = new URLSearchParams(params); next.delete("check"); setParams(next, { replace: true });
  }, [wanted, needList, params, setParams]);

  const openNeeds = (id: string) => setDrawer({ id, ids: needList.map((i) => i.id) });
  // Check all again: every row in Needs you that isn't already being worked on goes back in the queue.
  const checkable = needList.filter((i) => !actions.working(i));
  const ctx = drawer && drawer.ids.length > 1 ? { label: "Needs you", index: drawer.ids.indexOf(drawer.id), total: drawer.ids.length, prev: () => setDrawer({ ...drawer, id: drawer.ids[drawer.ids.indexOf(drawer.id) - 1] }), next: () => setDrawer({ ...drawer, id: drawer.ids[drawer.ids.indexOf(drawer.id) + 1] }) } : null;

  const needsCard = (
    <Card padding="none" style={{ overflow: "visible" }}>
      <CardHead pad="none" title="Needs you" count={needList.length} right={session.canAct && checkable.length > 0 ? <Button size="sm" variant="outline" leadingIcon={<Icon name="refresh-cw" size={14} />} onClick={() => actions.checkAgain(checkable.map((i) => i.id))}>Check all again</Button> : undefined} />
      {needs.isPending ? <LoadingRows rows={3} /> : needs.error ? <div style={{ padding: 20 }}><ErrorCard error={needs.error} retry={() => void needs.refetch()} /></div> : needRows.length === 0 ? <CardEmpty title="Nothing needs you" line={nextLine} /> : (
        <>
          <table className="avp-tbl avp-fx" style={table}>
            <colgroup><col style={{ width: 150 }} /><col style={{ width: 180 }} /><col style={{ width: 110 }} /><col /><col style={{ width: 230 }} /></colgroup>
            <thead><tr><ReceivedTh rel={rel} toggle={toggleRel} /><th style={th}>Supplier – invoice</th><th className="avp-sm" style={{ ...th, textAlign: "right" }}>Total</th><th style={th}>Check failed</th><th style={th}></th></tr></thead>
            <tbody>
              {needPaging.slice(needRows).map((i) => {
                const leaving = needLeaving.leaving[i.id];
                if (leaving) return <LeavingRow key={i.id} entry={leaving} home="needs" note={actions.notes[i.id]} rel={rel} currency={currency} onDone={needLeaving.done} onOpen={() => openNeeds(i.id)} />;
                const working = actions.working(i);
                const ladder = ladderFor(i, actions, { navigate });
                return (
                  <tr key={i.id} className="avp-row" onClick={() => openNeeds(i.id)} style={{ cursor: "pointer" }}>
                    <ReceivedTd i={i} rel={rel} />
                    <WhoTd i={i} onOpen={() => openNeeds(i.id)} />
                    <TotalTd i={i} currency={currency} />
                    <CheckFailedTd i={i} />
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                      <div className="avp-cw" style={cw}>
                        {working ? <Working text={actions.notes[i.id] ? actions.notes[i.id] + "…" : i.step ? stepWords(i.step) + "…" : "Queued for a check"} /> : !session.canAct ? null : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                            {ladder.primary && <LadderButton a={ladder.primary} />}
                            <LadderMenu ladder={ladder} open={menuFor === i.id} onToggle={() => setMenuFor(menuFor === i.id ? null : i.id)} />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager total={needRows.length} page={needPaging.page} per={needPaging.per} onPage={needPaging.setPage} onPer={needPaging.setPer} />
        </>
      )}
    </Card>
  );

  // The banner has three faces in one place: ready to approve with the buttons, the release's three stages, the receipt.
  const bannerBox = (quiet: boolean): React.CSSProperties => ({ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "14px 18px", margin: "16px 20px", borderRadius: "var(--radius-lg)", background: quiet ? "var(--surface-sunken)" : "var(--av-blue-50)", boxShadow: "inset 0 0 0 1px " + (quiet ? "var(--border-default)" : "var(--av-blue-200)") });
  const bannerTitle: React.CSSProperties = { font: "800 16px var(--font-display)", color: "var(--text-strong)" };
  const took = (s: number) => (s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`);
  const receiptBanner: ReactNode = receipt && !releasingNow ? (
    <div style={bannerBox(false)}>
      <CheckIcon r="ok" size={28} />
      <div style={{ flex: 1, minWidth: 220 }}>
        <b style={bannerTitle}>Approved in Syncore</b>
        <div className="avp-figs">
          <div><b className={receipt.approved ? "good" : "zero"}>{receipt.approved}</b>POs approved</div>
          {receipt.value !== null && <div><b>{money(receipt.value, currency)}</b>value</div>}
          {receipt.seconds > 0 && <div><b>{took(receipt.seconds)}</b>time taken</div>}
          {receipt.needs > 0 && <div><b className="warn">{receipt.needs}</b><a onClick={() => setTab("needs")} style={{ cursor: "pointer", color: "var(--status-review-fg)" }}>needs you</a></div>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Button size="sm" onClick={viewApproved}>View report</Button>
        <Button size="sm" variant="outline" onClick={dismissReceipt}>Dismiss</Button>
      </div>
    </div>
  ) : null;
  const readyBanner: ReactNode = readyCount > 0 ? (
    <div style={bannerBox(!!paused)}>
      <Icon name={paused ? "pause" : "calendar-clock"} size={20} style={{ color: paused ? "var(--text-muted)" : "var(--text-brand)" }} />
      <div style={{ flex: 1, minWidth: 220 }}><b style={bannerTitle}>{readyCount} ready to approve – <span style={mono}>{money(readyValue, currency)}</span></b><div style={muted}>{paused ? "Automatic approval paused – invoices stay ready; Approve now still works" : readyLine}</div></div>
      {session.canAct && openBatch && (paused
        ? <Button size="sm" leadingIcon={<Icon name="play" size={14} />} onClick={approveNow}>Approve now</Button>
        : <><Button size="sm" onClick={() => setDialog("approve")}>Approve now</Button>{policy !== "HoldForRelease" && <Button size="sm" variant="outline" onClick={() => setDialog("pause")}>Pause automatic approval…</Button>}</>)}
    </div>
  ) : null;
  const banner: ReactNode = releasingNow ? (
    <div style={bannerBox(false)}>
      <Spinner size={20} />
      <div style={{ flex: 1, minWidth: 220 }}>
        <b style={bannerTitle}>Approving {count(releaseTotal, "PO")} in Syncore</b>
        <div className="avp-stages">
          <span className="did"><Icon name="check" size={12} stroke={3} />Sent to the worker</span>
          {pickedUp
            ? <span className="did"><Icon name="check" size={12} stroke={3} />{pickedAfter !== null && pickedAfter >= 0 ? `Picked up after ${pickedAfter} s` : "Picked up"}</span>
            : <span className="on">Waiting for the worker – {waitedS} s</span>}
          {pickedUp ? <span className="on">Approving {releaseDone} of {releaseTotal}</span> : <span>Approving</span>}
        </div>
        {pickedUp && <span className="avp-pbar"><i style={{ width: `${releaseTotal ? Math.round((releaseDone / releaseTotal) * 100) : 0}%` }} /></span>}
        <div style={{ ...muted, marginTop: 10 }}>{pickedUp ? "" : "Usually under a minute. "}You can leave this page while approvals are processed.</div>
      </div>
    </div>
  ) : <>{receiptBanner}{readyBanner}</>;
  // During a release the table is a window of the batch's rows: the first page's worth, oldest first. Rows leave from
  // the top as they finish and the next ones move up into the window; the footer counts what is still waiting.
  const releaseShown = releaseRows.slice(0, readyPaging.per);
  const releaseRest = releaseRows.length - releaseShown.length;
  // Rows that were in the window when the release began don't fade in; the ones that move up later do.
  const firstWindow = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!rls) { firstWindow.current = null; return; }
    if (!firstWindow.current) firstWindow.current = new Set(rls.ids.slice(0, readyPaging.per));
  }, [rls, readyPaging.per]);
  const enters = (id: string) => releaseMode && !!firstWindow.current && !firstWindow.current.has(id);

  const readyCard = (
    <Card padding="none">
      <CardHead pad="none" title="Ready to approve" count={releaseMode ? releaseRows.length : readyCount} />
      {banner}
      {held.isPending ? <LoadingRows rows={3} /> : held.error ? <div style={{ padding: 20 }}><ErrorCard error={held.error} retry={() => void held.refetch()} /></div> : (releaseMode ? releaseRows : readyRows).length === 0 ? <CardEmpty title="Nothing is waiting for approval" line={nextLine} extra={approvedLink} /> : (
        <>
          <table className="avp-tbl avp-fx" style={table}>
            <colgroup><col style={{ width: 150 }} /><col style={{ width: 180 }} /><col style={{ width: 110 }} /><col /><col className="avp-md" style={{ width: 240 }} /></colgroup>
            <thead><tr><ReceivedTh rel={rel} toggle={toggleRel} /><th style={th}>Supplier – invoice</th><th className="avp-sm" style={{ ...th, textAlign: "right" }}>Total</th><th style={th}>Checks</th><th className="avp-md" style={th}>Syncore</th></tr></thead>
            <tbody>
              {(releaseMode ? releaseShown : readyPaging.slice(readyRows)).map((i) => {
                const leaving = releaseMode ? releaseEntry(i) : readyLeaving.leaving[i.id];
                if (leaving) return <LeavingRow key={i.id} entry={leaving} home="ready" rel={rel} currency={currency} onDone={releaseMode ? markFaded : readyLeaving.done} onOpen={() => navigate(`/invoices/${i.id}`)} fresh={batchFresh[i.id] ?? (releaseMode ? i : undefined)} enter={enters(i.id)} />;
                const url = syncoreUrl(i);
                return (
                  <tr key={i.id} className={"avp-row" + (enters(i.id) ? " avp-enter" : "")} onClick={() => navigate(`/invoices/${i.id}`)} style={{ cursor: "pointer" }}>
                    <ReceivedTd i={i} rel={rel} />
                    <WhoTd i={i} onOpen={() => navigate(`/invoices/${i.id}`)} />
                    <TotalTd i={i} currency={currency} />
                    <td style={td}><div className="avp-cw" style={cw}><ChecksOk i={i} /></div></td>
                    <td className="avp-md" style={{ ...td, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}><div className="avp-cw" style={cw}>{releasingNow || releaseMode ? <Working text={i.step ? stepWords(i.step) + "…" : "Queued for approval"} /> : url && poRef(i) ? <a href={url} target="_blank" rel="noopener" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>Open PO {poRef(i)}<Icon name="external-link" size={13} /></a> : <span style={{ color: "var(--text-subtle)" }}>—</span>}</div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {releaseMode
            ? releaseRest > 0 && <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", ...muted }}><b style={{ color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>{releaseRest} more</b> waiting to approve</div>
            : <Pager total={readyRows.length} page={readyPaging.page} per={readyPaging.per} onPage={readyPaging.setPage} onPer={readyPaging.setPer} />}
        </>
      )}
    </Card>
  );

  const loaded = !!needs.data && !!held.data;
  // Not "All caught up" while a release is running or its receipt is showing: the released rows are no longer held,
  // so the lists are empty, but the Ready card is where the release and the receipt live.
  const caughtUp = loaded && tab === "all" && needRows.length === 0 && readyRows.length === 0 && !releasingNow && !releaseMode && !receipt;

  return (
    <Page>
      <PageHeader title="Invoice Queue">
        {session.canAct && <><input ref={fileRef} type="file" accept={uploader.accept} multiple style={{ display: "none" }} onChange={(e) => { uploader.start(e.target.files); e.target.value = ""; }} /><Button variant="outline" leadingIcon={<Icon name="upload" size={16} />} loading={uploader.busy} onClick={() => fileRef.current?.click()}>Upload PDF</Button></>}
      </PageHeader>
      <UploadPanel items={[...uploader.items, ...recentUploads.items]} onDismiss={(key) => (key.startsWith("job:") ? recentUploads.dismiss(key) : uploader.dismiss(key))} style={{ marginBottom: 16 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
        <Tabs value={tab} onChange={(t) => setTab(t as Tab)} items={[{ value: "all", label: "Everything waiting", count: needList.length + readyCount }, { value: "needs", label: "Needs you", count: needList.length }, { value: "ready", label: "Ready to approve", count: readyCount }]} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {caughtUp ? (
          <Card padding="xl" style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <span style={{ width: 44, height: 44, borderRadius: 14, background: "var(--av-blue-50)", color: "var(--text-brand)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="check" size={20} /></span>
            <div style={{ flex: 1, minWidth: 240 }}><div style={{ font: "800 20px var(--font-display)", color: "var(--text-strong)" }}>All caught up</div><div style={{ font: "var(--type-body)", color: "var(--text-muted)", marginTop: 4 }}>{nextLine}</div></div>
            {approvedLink}
          </Card>
        ) : (
          <>
            {(tab === "needs" || (tab === "all" && needRows.length > 0)) && needsCard}
            {tab !== "needs" && readyCard}
          </>
        )}
      </div>
      {drawer && (
        <ProblemDrawer onClose={() => setDrawer(null)}>
          <ProblemDetail id={drawer.id} ctx={ctx} mode="drawer" onClose={() => setDrawer(null)} actions={actions} />
        </ProblemDrawer>
      )}
      {actions.dialogs}
      {dialog === "approve" && openBatch && <ApproveDialog count={readyCount} value={readyValue} currency={currency} policy={policy} nextRun={actions.nextRun} onCancel={() => setDialog(null)} onConfirm={() => { setDialog(null); approveNow(); }} />}
      {dialog === "pause" && openBatch && <PauseDialog count={readyCount} value={readyValue} currency={currency} nextRun={actions.nextRun} onCancel={() => setDialog(null)} onConfirm={(reason) => { setDialog(null); hold.mutate({ batch: openBatch, reason }, { onSuccess: () => toast({ tone: "info", title: "Automatic approval paused" }), onError: (e) => toast({ tone: "error", title: "Couldn’t pause", message: e.message }) }); }} />}
    </Page>
  );
}
